import type { TypedSupabaseClient } from '../client';
import type {
  IFacebookPixelRepository,
  FacebookPixel,
  CreateFacebookPixelInput,
  UpdateFacebookPixelInput,
  FacebookPixelDraft,
  StorefrontFacebookPixel,
  CapiEventInput,
  CapiSendResult,
  PurchaseEventStatus,
} from '../../../interfaces/src';

// The generated Supabase types are hand-maintained & stale, so reach the
// new table / RPC / function through narrow casts (same idiom as the other
// post-gen repositories — see delivery-providers-repository).
const anyFrom = (client: TypedSupabaseClient) =>
  (table: string) => (client as unknown as { from: (t: string) => any }).from(table);
const anyRpc = (client: TypedSupabaseClient) =>
  (fn: string, args: Record<string, unknown>) =>
    (client as unknown as { rpc: (f: string, a: Record<string, unknown>) => Promise<{ data: any; error: any }> }).rpc(fn, args);

export class SupabaseFacebookPixelsRepository implements IFacebookPixelRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(): Promise<FacebookPixel[]> {
    const { data, error } = await anyFrom(this.supabase)('facebook_pixels')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapPixel);
  }

  async create(input: CreateFacebookPixelInput): Promise<FacebookPixel> {
    const { data, error } = await anyFrom(this.supabase)('facebook_pixels')
      .insert(toRow(input))
      .select()
      .single();
    if (error) throw error;
    return mapPixel(data);
  }

  async update(id: string, input: UpdateFacebookPixelInput): Promise<FacebookPixel> {
    const { data, error } = await anyFrom(this.supabase)('facebook_pixels')
      .update(toRow(input))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapPixel(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('facebook_pixels').delete().eq('id', id);
    if (error) throw error;
  }

  /** Replace the tenant's full pixel set: delete removed rows, upsert the rest. */
  async replaceAll(drafts: FacebookPixelDraft[]): Promise<FacebookPixel[]> {
    const { data: current, error: listErr } = await anyFrom(this.supabase)('facebook_pixels').select('id');
    if (listErr) throw listErr;
    const currentIds: string[] = (current ?? []).map((r: { id: string }) => r.id);
    const keptIds = new Set(drafts.map((d) => d.id).filter(Boolean) as string[]);
    const toDelete = currentIds.filter((id) => !keptIds.has(id));
    if (toDelete.length) {
      const { error } = await anyFrom(this.supabase)('facebook_pixels').delete().in('id', toDelete);
      if (error) throw error;
    }
    const out: FacebookPixel[] = [];
    for (let i = 0; i < drafts.length; i += 1) {
      const d = drafts[i]!;
      const withOrder = { ...d, sortOrder: i };
      if (d.id) {
        // toRow() ignores unmapped keys (incl. `id`), so passing withOrder is safe.
        out.push(await this.update(d.id, withOrder));
      } else {
        out.push(await this.create(withOrder));
      }
    }
    return out;
  }

  async listForStorefront(tenantId: string): Promise<StorefrontFacebookPixel[]> {
    const { data, error } = await anyRpc(this.supabase)('get_storefront_facebook_pixels', { p_tenant_id: tenantId });
    // Soft-fail: if the RPC isn't available yet, the storefront just runs
    // without Facebook pixels rather than erroring on every page load.
    if (error) return [];
    return (data ?? []).map((r: Record<string, unknown>) => ({
      pixelId: String(r['pixel_id'] ?? ''),
      purchaseEventStatus: (r['purchase_event_status'] as PurchaseEventStatus) ?? 'pending',
      abandonedEventEnabled: Boolean(r['abandoned_event_enabled']),
      attributionPct: Number(r['attribution_pct'] ?? 100),
      domainVerification: (r['domain_verification'] as string) ?? null,
    }));
  }

  async sendCapiEvent(input: CapiEventInput): Promise<CapiSendResult> {
    const { data, error } = await this.supabase.functions.invoke('facebook-capi', { body: input });
    if (error) {
      // Don't throw on the storefront's fire-and-forget calls; surface a soft failure.
      return { ok: false, sent: 0, results: [], error: error instanceof Error ? error.message : String(error) };
    }
    return data as CapiSendResult;
  }
}

function toRow(input: UpdateFacebookPixelInput): Record<string, unknown> {
  const map: Record<string, string> = {
    pixelId: 'pixel_id',
    capiAccessToken: 'capi_access_token',
    testEventCode: 'test_event_code',
    isEnabled: 'is_enabled',
    purchaseEventStatus: 'purchase_event_status',
    abandonedEventEnabled: 'abandoned_event_enabled',
    attributionPct: 'attribution_pct',
    domainVerification: 'domain_verification',
    sortOrder: 'sort_order',
  };
  const row: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    const col = map[k];
    if (col) row[col] = v;
  }
  return row;
}

function mapPixel(row: Record<string, unknown>): FacebookPixel {
  return {
    id: row['id'] as string,
    pixelId: (row['pixel_id'] as string) ?? '',
    capiAccessToken: (row['capi_access_token'] as string) ?? null,
    testEventCode: (row['test_event_code'] as string) ?? null,
    isEnabled: Boolean(row['is_enabled']),
    purchaseEventStatus: (row['purchase_event_status'] as PurchaseEventStatus) ?? 'pending',
    abandonedEventEnabled: Boolean(row['abandoned_event_enabled']),
    attributionPct: Number(row['attribution_pct'] ?? 100),
    domainVerification: (row['domain_verification'] as string) ?? null,
    sortOrder: Number(row['sort_order'] ?? 0),
    createdAt: (row['created_at'] as string) ?? '',
    updatedAt: (row['updated_at'] as string) ?? '',
  };
}
